import { supabase } from './supabase'

/**
 * Who Holds What, B1 (154): job titles sorted into functions, typical holders
 * written by administrators, GRANT suggestions, and the answer log.
 *
 * ── NOTHING HERE DECIDES ANYTHING ───────────────────────────────────────────
 *
 * Every list is derived by the database when the page asks (role_suggestions
 * and friends are DEFINER functions with their own checks), and every act goes
 * through one RPC that checks again. This file shapes rows and names things.
 * There is deliberately no client-side filter or sort of a limited list
 * (standing rule 1): role_suggestions applies its own limit and says the
 * total. Since 159 the filters are its parameters, for the same reason.
 *
 * ── SUGGEST ONLY ─────────────────────────────────────────────────────────────
 *
 * Accept calls accept_role_suggestion, which calls assign_role — the same
 * function the person panel calls — so 137's privileged line and 153's region
 * line refuse here exactly as they refuse there. Decline records "incorrect",
 * reasons optional. Later hides a suggestion for 30 days and records nothing
 * (David, 10 Oct: "not now" is not a decline).
 */

/** The four reasons a decline may give. All optional (David, 10 Oct). */
export const DECLINE_REASONS = [
  { code: 'wrong_function', label: 'Wrong function for this title' },
  { code: 'not_this_person', label: 'Not this person' },
  { code: 'role_not_for_group', label: 'This role doesn’t go to this group' },
  { code: 'handled_otherwise', label: 'Already handled another way' },
] as const

export type DeclineReason = (typeof DECLINE_REASONS)[number]['code']

export function reasonLabel(code: string): string {
  return DECLINE_REASONS.find((r) => r.code === code)?.label ?? code
}

/** One grant suggestion: a person who looks like one of a role's typical holders. */
export type GrantSuggestion = {
  personId: string
  displayName: string
  jobTitle: string | null
  legalEntityName: string | null
  businessUnitName: string | null
  /** Ever signed in (rule 8: a fact about the invitation, not about activity). */
  signedIn: boolean
  roleKey: string
  roleName: string
  /** The grant's scope — the person's own legal entity for an entity-scoped holder. */
  orgUnitId: string | null
  orgUnitName: string | null
  holderId: string
  holderRevision: number
  holderName: string
  holderWhy: string
  /** What matched. 'function' means a title SORT matched — the machine's part. */
  matchedOn: 'title' | 'function' | 'department'
  functionKey: string | null
  functionLabel: string | null
  /** 'one' when OneConetec sorted the title and HR confirmed it; 'person' when HR sorted it. */
  sortedBy: 'one' | 'person' | null
  /** What a decline holds against. Sent back with every answer. */
  evidenceMd5: string
}

export type SuggestionPage = { suggestions: GrantSuggestion[]; total: number }

/**
 * 159. The Suggestions tab's filters — the People tab's four, matched by the
 * database the way people_roster matches them. Empty means no filter. Several
 * values in one filter are OR'd; the filters are AND'd.
 */
export type SuggestionFilters = {
  search?: string
  /** Org unit ids: a Where choice already turned into units (lib/where.ts). */
  whereIds?: string[]
  departmentIds?: string[]
  jobTitles?: string[]
  /** The SUGGESTED role. */
  roleKeys?: string[]
}

export async function loadGrantSuggestions(limit = 200, filters: SuggestionFilters = {}): Promise<SuggestionPage> {
  // Every argument, always: the 154 two-argument form still exists, and
  // naming all seven is what makes PostgREST pick this one.
  const { data, error } = await supabase.rpc('role_suggestions', {
    p_kind: 'grant',
    p_limit: limit,
    p_search: filters.search?.trim() || null,
    p_where: filters.whereIds ?? [],
    p_departments: filters.departmentIds ?? [],
    p_job_titles: filters.jobTitles ?? [],
    p_roles: filters.roleKeys ?? [],
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    total: rows.length ? Number(rows[0].total_count) : 0,
    suggestions: rows.map((r) => ({
      personId: r.person_id as string,
      displayName: r.display_name as string,
      jobTitle: (r.job_title as string | null) ?? null,
      legalEntityName: (r.legal_entity_name as string | null) ?? null,
      businessUnitName: (r.business_unit_name as string | null) ?? null,
      signedIn: r.signed_in === true,
      roleKey: r.role_key as string,
      roleName: r.role_name as string,
      orgUnitId: (r.org_unit_id as string | null) ?? null,
      orgUnitName: (r.org_unit_name as string | null) ?? null,
      holderId: r.holder_id as string,
      holderRevision: Number(r.holder_revision),
      holderName: r.holder_name as string,
      holderWhy: r.holder_why as string,
      matchedOn: r.matched_on as GrantSuggestion['matchedOn'],
      functionKey: (r.function_key as string | null) ?? null,
      functionLabel: (r.function_label as string | null) ?? null,
      sortedBy: (r.sorted_by as GrantSuggestion['sortedBy']) ?? null,
      evidenceMd5: r.evidence_md5 as string,
    })),
  }
}

/** "Add Gallery document user", or "Add HR Administrator for GEOTECH". */
export function suggestionAction(s: Pick<GrantSuggestion, 'roleName' | 'orgUnitName'>): string {
  return `Add ${s.roleName}${s.orgUnitName ? ` for ${s.orgUnitName}` : ''}`
}

/**
 * The one line on a suggestion that a MACHINE decided, or null. Only a match
 * through a title sort One made is the machine's; a person's sort, a title or
 * a department match is not, and must not wear teal (the AI disclosure).
 */
export function machineLine(s: Pick<GrantSuggestion, 'matchedOn' | 'sortedBy' | 'jobTitle' | 'functionLabel'>): string | null {
  if (s.matchedOn !== 'function' || s.sortedBy !== 'one') return null
  return `OneConetec sorted “${s.jobTitle ?? 'this title'}” as ${s.functionLabel ?? 'this function'} · confirmed by HR`
}

/** Plain words for a match that was not the machine's. */
export function matchLine(s: Pick<GrantSuggestion, 'matchedOn' | 'sortedBy' | 'jobTitle' | 'functionLabel'>): string | null {
  if (s.matchedOn === 'title') return `Matched on the job title “${s.jobTitle ?? ''}”`
  if (s.matchedOn === 'department') return 'Matched on their department'
  if (s.sortedBy === 'person') return `HR sorted “${s.jobTitle ?? 'this title'}” as ${s.functionLabel ?? 'this function'}`
  return null
}

/**
 * Accept = assign_role. `orgUnitIds` null grants as suggested; otherwise one
 * grant per unit, all or nothing (159) — a region granted half-way is the one
 * nobody notices.
 */
export async function acceptSuggestion(
  s: Pick<GrantSuggestion, 'personId' | 'holderId' | 'evidenceMd5'>,
  orgUnitIds: string[] | null = null,
): Promise<void> {
  const { error } = await supabase.rpc('accept_role_suggestion', {
    p_person_id: s.personId,
    p_holder_id: s.holderId,
    p_evidence_md5: s.evidenceMd5,
    p_org_unit_ids: orgUnitIds,
  })
  if (error) throw error
}

/** 159. One act over several suggestions. */
export type BulkAnswer = 'accept' | 'decline' | 'later'

/** What came back for one item: done, or the server's refusal in its own words. */
export type BulkResult = { personId: string; holderId: string; displayName: string; done: boolean; why: string | null }

/**
 * 159. answer_role_suggestions: each item through the single-answer function,
 * in its own subtransaction — not all-or-nothing, because these are separate
 * decisions about separate people. `scopes` carries a scope chosen on a row
 * (accept only); a row without one is granted as suggested.
 */
export async function answerSuggestions(
  answer: BulkAnswer,
  items: Pick<GrantSuggestion, 'personId' | 'holderId' | 'evidenceMd5'>[],
  scopes: Record<string, string[]> = {},
  reasons: DeclineReason[] = [],
  note = '',
): Promise<BulkResult[]> {
  const { data, error } = await supabase.rpc('answer_role_suggestions', {
    p_answer: answer,
    p_items: items.map((s) => {
      const scope = answer === 'accept' ? scopes[suggestionToken(s)] : undefined
      return {
        person_id: s.personId,
        holder_id: s.holderId,
        evidence_md5: s.evidenceMd5,
        ...(scope ? { org_unit_ids: scope } : {}),
      }
    }),
    p_reason_codes: answer === 'decline' && reasons.length ? reasons : null,
    p_reason_text: answer === 'decline' ? note.trim() || null : null,
  })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    personId: r.person_id as string,
    holderId: r.holder_id as string,
    displayName: (r.display_name as string | null) ?? 'Someone',
    done: r.done === true,
    why: (r.why as string | null) ?? null,
  }))
}

/** One suggestion's key on the page: a person and the typical holder that suggested them. */
export function suggestionToken(s: Pick<GrantSuggestion, 'personId' | 'holderId'>): string {
  return `${s.personId}:${s.holderId}`
}

/** The outcome line after a bulk answer. The misses are listed by name beneath it. */
export function summariseBulk(answer: BulkAnswer, results: BulkResult[]): string {
  const done = results.filter((r) => r.done).length
  const missed = results.length - done
  const verb = answer === 'accept' ? 'Accepted' : answer === 'decline' ? 'Declined' : 'Put off for 30 days'
  const what =
    answer === 'accept'
      ? ' — granted, and recorded as correct suggestions.'
      : answer === 'decline'
        ? ' — nothing was granted.'
        : ' — nothing was recorded.'
  const past = answer === 'accept' ? 'accepted' : answer === 'decline' ? 'declined' : 'put off'
  const head = done > 0 ? `${verb}: ${done}${what}` : `Nothing was ${past}.`
  return missed > 0 ? `${head} ${missed} not done — see why below.` : head
}

export async function declineSuggestion(
  s: Pick<GrantSuggestion, 'personId' | 'holderId' | 'evidenceMd5'>,
  reasons: DeclineReason[],
  note: string,
): Promise<void> {
  const { error } = await supabase.rpc('decline_role_suggestion', {
    p_person_id: s.personId,
    p_holder_id: s.holderId,
    p_evidence_md5: s.evidenceMd5,
    // Empty means "no reason" — the server stores NULL, never '{}'.
    p_reason_codes: reasons.length ? reasons : null,
    p_reason_text: note.trim() || null,
  })
  if (error) throw error
}

/** Hidden for 30 days for everyone who could act on it. Returns when it comes back. */
export async function laterSuggestion(s: Pick<GrantSuggestion, 'personId' | 'holderId'>): Promise<string> {
  const { data, error } = await supabase.rpc('later_role_suggestion', {
    p_person_id: s.personId,
    p_holder_id: s.holderId,
  })
  if (error) throw error
  return data as string
}

/** A settled ruling the tab honours, in its own words (ACC-6, B14). */
export type Ruling = { roleKey: string; roleName: string; kind: string; ref: string; words: string; decidedAt: string }

export async function loadRulings(): Promise<Ruling[]> {
  const { data, error } = await supabase.rpc('role_suggestion_rulings')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    roleKey: r.role_key as string,
    roleName: r.role_name as string,
    kind: r.kind as string,
    ref: r.ruling_ref as string,
    words: r.words as string,
    decidedAt: r.decided_at as string,
  }))
}

/**
 * One answer. Who answered is the audit trail: it is listed, never counted or
 * ranked (B22). The typed reason is readable here only because the server
 * lists only people the caller may grant to.
 */
export type SuggestionAnswer = {
  outcomeId: string
  personId: string
  displayName: string
  roleKey: string
  roleName: string
  orgUnitName: string | null
  holderId: string
  holderName: string
  outcome: 'correct' | 'incorrect'
  reasonCodes: string[]
  reasonText: string | null
  jobTitle: string | null
  decidedByName: string | null
  decidedAt: string
  /** The suggestion still stands, so a decline can be changed to an accept (a new answer). */
  stillSuggested: boolean
  currentEvidenceMd5: string | null
}

export async function loadAnswers(limit = 50): Promise<SuggestionAnswer[]> {
  const { data, error } = await supabase.rpc('role_suggestion_answers', { p_limit: limit })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    outcomeId: r.outcome_id as string,
    personId: r.person_id as string,
    displayName: r.display_name as string,
    roleKey: r.role_key as string,
    roleName: r.role_name as string,
    orgUnitName: (r.org_unit_name as string | null) ?? null,
    holderId: r.holder_id as string,
    holderName: r.holder_name as string,
    outcome: r.outcome as 'correct' | 'incorrect',
    reasonCodes: (r.reason_codes as string[] | null) ?? [],
    reasonText: (r.reason_text as string | null) ?? null,
    jobTitle: (r.job_title as string | null) ?? null,
    decidedByName: (r.decided_by_name as string | null) ?? null,
    decidedAt: r.decided_at as string,
    stillSuggested: r.still_suggested === true,
    currentEvidenceMd5: (r.current_evidence_md5 as string | null) ?? null,
  }))
}

/** What is waiting for a person before suggestions can be trusted. */
export type SuggestionSetup = {
  /** Tenant administrator or group HR: the company-wide vocabulary is theirs. */
  maySetUp: boolean
  unsorted: number
  /** Titles whose only sorts were rejected: HR sorts these by hand. */
  unsortedRejected: number
  toCheck: number
  functionsDraft: number
  holdersDraft: number
  /** 303. Per function: OneConetec's sorts nobody has accepted, and the confirmed holders that name it. */
  waiting: WaitingFunction[]
}

/**
 * 303. A function whose job titles OneConetec sorted and nobody has accepted.
 * Nothing matches on a suggestion (154), so a typical holder that names the
 * function matches nobody through it — which is what David saw on 22 Oct
 * after adding Operations management to a confirmed holder: 0 accepted sorts,
 * 19 suggested, and a tab that was right not to change.
 */
export type WaitingFunction = {
  functionKey: string
  labelEn: string | null
  labelEs: string | null
  /** Job titles waiting, and the active people who hold them. */
  titles: number
  people: number
  /** Names of the CONFIRMED typical holders that name this function. */
  holders: string[]
}

/**
 * 303. The one sentence for a function whose sorts are waiting — printed on
 * the Suggestions tab and on each typical holder that names the function
 * (rule 11: one wording, two surfaces).
 */
export function waitingWords(w: WaitingFunction, lang: 'en' | 'es' = 'en'): string {
  const t = `${w.titles} job title${w.titles === 1 ? '' : 's'}`
  const p = `${w.people} ${w.people === 1 ? 'person' : 'people'}`
  return `${functionName(w, lang)}: ${t} OneConetec sorted (${p}) are waiting in Job titles — nothing matches through this function until a person accepts them.`
}

export async function loadSetup(): Promise<SuggestionSetup> {
  const { data, error } = await supabase.rpc('role_suggestion_setup')
  if (error) throw error
  const j = (data ?? {}) as Record<string, unknown>
  return {
    maySetUp: j.may_set_up === true,
    unsorted: Number(j.unsorted ?? 0),
    unsortedRejected: Number(j.unsorted_rejected ?? 0),
    toCheck: Number(j.to_check ?? 0),
    functionsDraft: Number(j.functions_draft ?? 0),
    holdersDraft: Number(j.holders_draft ?? 0),
    waiting: ((j.waiting as Record<string, unknown>[] | null) ?? []).map((w) => ({
      functionKey: w.function_key as string,
      labelEn: (w.label_en as string | null) ?? null,
      labelEs: (w.label_es as string | null) ?? null,
      titles: Number(w.titles ?? 0),
      people: Number(w.people ?? 0),
      holders: (w.holders as string[] | null) ?? [],
    })),
  }
}

/**
 * Ask OneConetec to sort titles nobody has sorted: the role-titles Edge
 * Function, as the caller, titles only. Called when the tab opens and unsorted
 * titles exist — there is no schedule (standing rule 2). Up to `rounds` calls
 * of 80 titles each, stopping as soon as one sorts nothing.
 */
export async function sortNewTitles(rounds = 4): Promise<{ sorted: number; error: string | null }> {
  let sorted = 0
  for (let i = 0; i < rounds; i++) {
    const { data, error } = await supabase.functions.invoke('role-titles', { body: { kind: 'sort' } })
    if (error) return { sorted, error: await readFunctionError(error) }
    const d = (data ?? {}) as { sorted?: number; sent?: number; error?: string }
    if (d.error) return { sorted, error: d.error }
    sorted += d.sorted ?? 0
    if (!d.sorted || !d.sent) break
  }
  return { sorted, error: null }
}

/**
 * Say WHICH failure it was. The first version answered every failure with one
 * sentence, and the first time it showed (10 Oct) the cause — role-titles not
 * yet deployed to the project, a 404 — was invisible on the page.
 */
export async function readFunctionError(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context
  const status = ctx && typeof ctx.status === 'number' ? ctx.status : null
  let bodyError: string | null = null
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: string }
      if (body?.error) bodyError = body.error
    } catch {
      /* fall through */
    }
  }
  return functionErrorWords(status, bodyError)
}

/** The sentence for a failed call to role-titles: its own words, else what the status means. */
export function functionErrorWords(status: number | null, bodyError: string | null): string {
  if (bodyError) return bodyError
  if (status === 404) return 'the title-sorting function (role-titles) is not deployed to this database yet'
  if (status === 401) return 'your sign-in was not accepted by the title-sorting function — sign in again'
  if (status) return `the title-sorting function answered ${status}`
  return 'the title-sorting function could not be reached (network or CORS)'
}

/* ── functions and title sorts ─────────────────────────────────────────────── */

export type JobFunction = {
  key: string
  /** Either name may be missing — not translated yet (200). Never both. */
  labelEn: string | null
  labelEs: string | null
  sortOrder: number
  /** False for the assistant's draft nobody has read yet. */
  confirmed: boolean
  retired: boolean
  titles: number
}

export async function loadFunctions(): Promise<JobFunction[]> {
  const { data, error } = await supabase.rpc('job_functions')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    key: r.key as string,
    labelEn: (r.label_en as string | null) ?? null,
    labelEs: (r.label_es as string | null) ?? null,
    sortOrder: Number(r.sort_order),
    confirmed: r.confirmed === true,
    retired: r.retired === true,
    titles: Number(r.titles ?? 0),
  }))
}

/**
 * A function's name in the reader's language, or the other one when it has
 * not been translated yet (200) — the same fallback as app.in_my_language.
 */
export function functionName(f: Pick<JobFunction, 'labelEn' | 'labelEs'>, lang: 'en' | 'es' = 'en'): string {
  const en = f.labelEn?.trim() || null
  const es = f.labelEs?.trim() || null
  return (lang === 'es' ? es ?? en : en ?? es) ?? ''
}

/**
 * Add, relabel, confirm or bring back a function. Saving is confirming.
 * A null key is a NEW function: the server makes its key from the name (200).
 * A blank name is sent as null — "not translated yet", never a copy.
 */
export async function saveFunction(
  f: { key: string | null; labelEn: string | null; labelEs: string | null; sortOrder: number },
): Promise<void> {
  const { error } = await supabase.rpc('save_job_function', {
    p_key: f.key,
    p_label_en: f.labelEn?.trim() || null,
    p_label_es: f.labelEs?.trim() || null,
    p_sort_order: f.sortOrder,
  })
  if (error) throw error
}

/**
 * "Confirm all drafts" (200): confirms exactly the drafts the person was shown,
 * not whatever is a draft by the time the call lands. Returns how many.
 */
export async function confirmFunctions(keys: string[]): Promise<number> {
  const { data, error } = await supabase.rpc('confirm_job_functions', { p_keys: keys })
  if (error) throw error
  return Number(data ?? 0)
}

export async function retireFunction(key: string): Promise<void> {
  const { error } = await supabase.rpc('retire_job_function', { p_key: key })
  if (error) throw error
}

export type TitleSort = {
  jobTitle: string
  people: number
  functionKey: string | null
  status: 'suggested' | 'accepted' | null
  sortedBy: 'one' | 'person' | null
  confidence: number | null
  /** Functions this title was rejected from. A rejected sort stays rejected. */
  rejected: string[]
}

export async function loadTitleSorts(): Promise<TitleSort[]> {
  const { data, error } = await supabase.rpc('title_sorts')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    jobTitle: r.job_title as string,
    people: Number(r.people),
    functionKey: (r.function_key as string | null) ?? null,
    status: (r.status as TitleSort['status']) ?? null,
    sortedBy: (r.sorted_by as TitleSort['sortedBy']) ?? null,
    confidence: r.confidence === null || r.confidence === undefined ? null : Number(r.confidence),
    rejected: (r.rejected as string[] | null) ?? [],
  }))
}

/** Which list a title belongs in on the Job titles tray. */
export type TitleState = 'to_check' | 'unsorted' | 'sorted'

export function titleState(t: Pick<TitleSort, 'status'>): TitleState {
  return t.status === 'suggested' ? 'to_check' : t.status === 'accepted' ? 'sorted' : 'unsorted'
}

/**
 * 303. Accept every one of OneConetec's suggested sorts into one function at
 * once — the function row lists them first. Returns how many were accepted;
 * the server refuses when nothing is suggested (rule 12).
 */
export async function acceptTitleSorts(functionKey: string): Promise<number> {
  const { data, error } = await supabase.rpc('accept_title_sorts', { p_function_key: functionKey })
  if (error) throw error
  return Number(data ?? 0)
}

export async function decideTitleSort(
  jobTitle: string,
  decision: 'accept' | 'reject' | 'set',
  functionKey: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('decide_title_sort', {
    p_job_title: jobTitle,
    p_decision: decision,
    p_function_key: functionKey,
  })
  if (error) throw error
}

/* ── typical holders ───────────────────────────────────────────────────────── */

export type TypicalHolder = {
  holderId: string
  revision: number
  roleKey: string
  roleName: string
  scopeTo: 'none' | 'entity'
  source: 'admin' | 'drafted_by_one'
  status: 'draft' | 'confirmed' | 'retired'
  nameEn: string
  nameEs: string | null
  descriptionEn: string
  descriptionEs: string | null
  whyEn: string
  whyEs: string | null
  titles: string[]
  functions: string[]
  departments: string[]
  /** null = every legal entity. */
  legalEntities: string[] | null
  excludedTitles: string[]
  excludedEntities: string[]
  writtenByName: string | null
  writtenAt: string
  /** Counts for group administrators; null for a regional holder. */
  matches: number | null
  signedIn: number | null
}

export async function loadTypicalHolders(includeRetired = false): Promise<TypicalHolder[]> {
  const { data, error } = await supabase.rpc('typical_holders', { p_include_retired: includeRetired })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    holderId: r.holder_id as string,
    revision: Number(r.revision),
    roleKey: r.role_key as string,
    roleName: r.role_name as string,
    scopeTo: r.scope_to as TypicalHolder['scopeTo'],
    source: r.source as TypicalHolder['source'],
    status: r.status as TypicalHolder['status'],
    nameEn: r.name_en as string,
    nameEs: (r.name_es as string | null) ?? null,
    descriptionEn: r.description_en as string,
    descriptionEs: (r.description_es as string | null) ?? null,
    whyEn: r.why_en as string,
    whyEs: (r.why_es as string | null) ?? null,
    titles: (r.titles as string[] | null) ?? [],
    functions: (r.functions as string[] | null) ?? [],
    departments: (r.departments as string[] | null) ?? [],
    legalEntities: (r.legal_entities as string[] | null) ?? null,
    excludedTitles: (r.excluded_titles as string[] | null) ?? [],
    excludedEntities: (r.excluded_entities as string[] | null) ?? [],
    writtenByName: (r.written_by_name as string | null) ?? null,
    writtenAt: r.written_at as string,
    matches: r.matches === null || r.matches === undefined ? null : Number(r.matches),
    signedIn: r.signed_in === null || r.signed_in === undefined ? null : Number(r.signed_in),
  }))
}

/**
 * The confirmed typical holders' words, by role key — what the grant panel
 * prints beside each role so somebody granting by hand reads the same
 * description a suggestion carries.
 */
export function holderWordsByRole(
  holders: Pick<TypicalHolder, 'roleKey' | 'status' | 'nameEn' | 'nameEs' | 'whyEn' | 'whyEs'>[],
  locale = 'en-CA',
): Record<string, { name: string; why: string }[]> {
  const es = locale.startsWith('es')
  const out: Record<string, { name: string; why: string }[]> = {}
  for (const h of holders) {
    if (h.status !== 'confirmed') continue
    ;(out[h.roleKey] ??= []).push({
      name: (es && h.nameEs) || h.nameEn,
      why: (es && h.whyEs) || h.whyEn,
    })
  }
  return out
}

/** A REPLACE: every field, no defaults (rule 9). */
export type TypicalHolderDraft = Omit<
  TypicalHolder,
  'revision' | 'roleName' | 'source' | 'status' | 'writtenByName' | 'writtenAt' | 'matches' | 'signedIn' | 'holderId'
> & { holderId: string | null }

export async function saveTypicalHolder(d: TypicalHolderDraft): Promise<{ holderId: string; revision: number }> {
  const { data, error } = await supabase.rpc('save_typical_holder', {
    p_holder_id: d.holderId,
    p_role_key: d.roleKey,
    p_scope_to: d.scopeTo,
    p_name_en: d.nameEn,
    p_name_es: d.nameEs,
    p_description_en: d.descriptionEn,
    p_description_es: d.descriptionEs,
    p_why_en: d.whyEn,
    p_why_es: d.whyEs,
    p_titles: d.titles,
    p_functions: d.functions,
    p_departments: d.departments,
    // An empty list means every entity, which the server stores as NULL.
    p_legal_entities: d.legalEntities && d.legalEntities.length ? d.legalEntities : null,
    p_excluded_titles: d.excludedTitles,
    p_excluded_entities: d.excludedEntities,
  })
  if (error) throw error
  const j = (data ?? {}) as { holder_id?: string; revision?: number }
  return { holderId: j.holder_id as string, revision: Number(j.revision) }
}

export async function setTypicalHolderStatus(holderId: string, status: TypicalHolder['status']): Promise<void> {
  const { error } = await supabase.rpc('set_typical_holder_status', { p_holder_id: holderId, p_status: status })
  if (error) throw error
}

/** The status in words, for the pill. */
export const HOLDER_STATUS_LABEL: Record<TypicalHolder['status'], string> = {
  draft: 'Draft — suggests nobody',
  confirmed: 'Confirmed',
  retired: 'Retired',
}

/** The roles a typical holder may be written for (154 refuses the rest). */
export function holderRoleAllowed(key: string): boolean {
  return key !== 'gallery_browser' && key !== 'employee' && key !== 'store_manager'
}
